import Build from '@mui/icons-material/Build';
import DirtyLens from '@mui/icons-material/DirtyLens';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import useFetchDelivery from 'api/hooks/useFetchDelivery';
import { useUpdateTrayInspection } from 'api/hooks/useUpdateTrayInspection';
import { booleanInspectionResults, TrayInspectionInfo, TrayInspectionResults } from 'api/models/Trays';
import { DropTableContainer } from 'components/DeliveryListPage/styles';
import { queryClient } from 'components/QueryClientProvider/QueryClientProvider';
import { useLanguage } from 'context/AppContext';
import useTraySubmitPage from '../hooks/useTraySubmitPage';
import { CheckOutTrayNotUsedConfirmDialog } from './CheckOutTrayNotUsedConfirmDialog';
import MissingDevicesModal from './MissingDevicesModal';
import MissingDevicesReasonModal from './MissingDevicesReasonModal';
import PassFailButtons from './PassFailButtons';
import routes from 'routes';

export type InspectionChangeHandler = (key: booleanInspectionResults | 'missingParts', val: boolean | string[]) => void;

type TPreImageCaptureInspection = FC<{
  inspection: TrayInspectionInfo;
  onSetImageToCheckIn?: (val: string) => Promise<void>;
  onChange?: InspectionChangeHandler;
  disabled?: boolean;
  trayId?: string;
  collectMissingDevices?: boolean;
  onUpdateMissingPartsComment?: (comment: string) => void;
}>;

const PreImageCaptureInspection: TPreImageCaptureInspection = ({
  inspection,
  onChange,
  disabled,
  onSetImageToCheckIn,
  trayId: passedTrayId,
  collectMissingDevices = false,
  onUpdateMissingPartsComment,
}) => {
  const showInspectionIcons = false;
  const [missingDevicesModalOpen, setMissingDevicesModalOpen] = useState(false);
  const [devicesCleanOfDebris, setDevicesCleanOfDebris] = useState<boolean>(inspection.allClean);
  const [allDevicesFunctional, setAllDevicesFunctional] = useState<boolean>(inspection.allFunctional);
  const [missingDevices, setMissingDevices] = useState<boolean>(inspection.allPartsPresent);
  const [trayUsed, setTrayUsed] = useState<TrayInspectionResults['trayUsed']>(inspection.trayUsed);
  const lang = useLanguage()[0].CheckInTrayPage;
  const navigate = useNavigate();
  const [trayNotUsedModalOpen, setTrayNotUsedModalOpen] = useState(false);
  const { handleFormSubmit } = useTraySubmitPage(inspection.stage === 'CHECK_OUT' ? 'out' : 'in');
  const { deliveryId = '', setId = '', trayId = passedTrayId || '' } = useParams();
  const { tray, delivery, set } = useFetchDelivery(deliveryId, setId, trayId);
  const { data: user } = useCurrentUser();
  const [missingPartsComment, setMissingPartsComment] = useState<string>(inspection.missingPartsComment);

  // saved inspection state to the api and reloads the delivery
  const { mutateAsync: updateTrayInspection } = useUpdateTrayInspection();

  const [missingDevicesReasonModalOpen, setMissingDevicesReasonModalOpen] = useState(false);

  const handleUpdateMissingPartsComment = useCallback(
    (comment: string) => {
      setMissingPartsComment(comment);
      onUpdateMissingPartsComment?.(comment);
      queryClient.invalidateQueries(['trayCapturePrintOut', deliveryId, tray]);
    },
    [deliveryId, onUpdateMissingPartsComment, tray],
  );

  // update checked state if inpections changes
  useEffect(() => {
    setDevicesCleanOfDebris(inspection.allClean);
    setAllDevicesFunctional(inspection.allFunctional);
    setMissingDevices(inspection.allPartsPresent);
    setTrayUsed(inspection.trayUsed);
    setMissingPartsComment(inspection.missingPartsComment);
  }, [
    inspection.allClean,
    inspection.allFunctional,
    inspection.allPartsPresent,
    inspection.trayUsed,
    inspection.gsImagePath,
    inspection.missingPartsComment,
  ]);

  const handleDebrisButtonClick = useCallback(
    (isPass: boolean) => {
      setDevicesCleanOfDebris(isPass);
      onChange?.('allClean', isPass);
    },
    [onChange],
  );

  const handleAllDevicesFunctionalButtonClick = useCallback(
    (isPass: boolean) => {
      setAllDevicesFunctional(isPass);
      onChange?.('allFunctional', isPass);
    },
    [onChange],
  );

  const handleTrayUsedClick = useCallback(
    (isPass: boolean) => {
      setTrayUsed(isPass);
      onChange?.('trayUsed', isPass);
      if (!isPass) {
        setTrayNotUsedModalOpen(true);
      }
    },
    [onChange],
  );

  const handleMissingDevicesConfirm = useCallback(
    (missingDevices: string[]) => {
      onChange?.('missingParts', missingDevices);
      setMissingDevicesModalOpen(false);
    },
    [onChange],
  );

  const handleSetTrayStatusToConfirmed = useCallback(async () => {
    if (!delivery) {
      throw new Error('Delivery not found');
    }

    if (!set) {
      throw new Error('Set not found');
    }

    if (!tray) {
      throw new Error('Tray not found');
    }

    if (!user) {
      throw new Error('User not found');
    }

    const ins = { ...inspection };
    if (ins.status == 'CONFIRMED') return;
    ins.status = 'CONFIRMED';
    inspection.status = 'CONFIRMED';
    inspection.missingPartsComment = missingPartsComment;

    // if we're checking out the tray
    // log complete tray check out event
    const isTrayCheckOut = ins.stage === 'CHECK_OUT';

    if (isTrayCheckOut) {
      await trackEvent(EventType.CompleteTrayCheckOut, {
        status: delivery.status,
        order_id: delivery.orderNumber,
        received_by: delivery?.receivedDetails?.receivedBy || '',
        set_id: set?.kitInstanceId || '',
        tray_instance_id: trayId,
        all_clean: inspection?.allClean,
        all_functional: inspection.allFunctional,
        all_parts_present: inspection.allPartsPresent,
        tray_used: inspection.trayUsed,
        current_user: user?.userId || '',
        tenant_name: user?.tenantName || '',
      });
    }

    await updateTrayInspection({
      trayId: trayId,
      inspectionInfo: ins,
    });
  }, [delivery, set, tray, user, inspection, missingPartsComment, updateTrayInspection, trayId]);

  const unusedTrayProceed = useCallback(async () => {
    setDevicesCleanOfDebris(true);
    setAllDevicesFunctional(true);
    setMissingDevices(true);
    setTrayUsed(false);
    setMissingPartsComment('');

    if (onChange) {
      onChange('allClean', true);
      onChange('allFunctional', true);
      onChange('allPartsPresent', true);
      onChange('trayUsed', false);
    }

    await handleSetTrayStatusToConfirmed();

    if (onSetImageToCheckIn) {
      const checkInInspection = tray?.inspections.find(i => i.stage == 'CHECK_IN');

      if (checkInInspection) {
        await onSetImageToCheckIn(checkInInspection.gsImagePath);
      }
    }

    await handleFormSubmit();

    navigate(routes.SetDetails.makePath(deliveryId, setId));
  }, [
    deliveryId,
    handleFormSubmit,
    handleSetTrayStatusToConfirmed,
    navigate,
    onChange,
    onSetImageToCheckIn,
    setId,
    tray?.inspections,
  ]);

  const isDisabled = !onChange || disabled;

  const missingDevicesClick = useCallback(
    (isPass: boolean) => {
      if (!isPass) {
        if (collectMissingDevices) setMissingDevicesModalOpen(true);
        else setMissingDevicesReasonModalOpen(true);
      }
      setMissingDevices(isPass);
      onChange?.('allPartsPresent', isPass);
    },
    [collectMissingDevices, onChange],
  );

  return (
    <>
      <DropTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{lang.qualityCheck}</TableCell>
              <TableCell sx={{ width: '240px' }}>{lang.result}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* TODO: add key back to tableRow later */}
            {/*<TableRow key={delivery.deliveryId}> */}
            <TableRow>
              <TableCell>
                {showInspectionIcons && <DirtyLens sx={{ marginBottom: '-4px', height: '22px' }} />}
                {lang.allDevicesClean}
              </TableCell>
              <TableCell>
                <PassFailButtons check={devicesCleanOfDebris} click={handleDebrisButtonClick} disabled={isDisabled} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {showInspectionIcons && <MiscellaneousServicesIcon sx={{ marginBottom: '-4px', height: '22px' }} />}
                {lang.allDevicesFunctional}
              </TableCell>
              <TableCell>
                <PassFailButtons
                  check={allDevicesFunctional}
                  click={handleAllDevicesFunctionalButtonClick}
                  disabled={isDisabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {showInspectionIcons && <Build sx={{ marginBottom: '-4px', height: '20px' }} />}
                {lang.allDevicesInTray}
              </TableCell>
              <TableCell>
                <PassFailButtons
                  check={missingDevices}
                  click={missingDevicesClick}
                  altFailText={collectMissingDevices ? lang.listMissingDevicesButtonText : undefined}
                  disabled={isDisabled}
                />
              </TableCell>
            </TableRow>
            {inspection.stage === 'CHECK_OUT' && (
              <TableRow>
                <TableCell>
                  {showInspectionIcons && <RepeatIcon sx={{ marginBottom: '-4px', height: '20px' }} />}
                  {lang.trayUsed}
                </TableCell>
                <TableCell>
                  <PassFailButtons
                    check={trayUsed}
                    click={handleTrayUsedClick}
                    altFailText={lang.no}
                    altPassText={lang.yes}
                    disabled={isDisabled}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DropTableContainer>
      <CheckOutTrayNotUsedConfirmDialog
        isOpen={trayNotUsedModalOpen}
        onCancel={() => setTrayNotUsedModalOpen(false)}
        onConfirm={unusedTrayProceed}
      />
      <MissingDevicesModal
        open={missingDevicesModalOpen}
        close={() => setMissingDevicesModalOpen(false)}
        deliveryId={deliveryId || ''}
        inspection={inspection}
        trayId={trayId}
        onConfirmMissingDevices={collectMissingDevices ? handleMissingDevicesConfirm : undefined}
      />
      <MissingDevicesReasonModal
        open={missingDevicesReasonModalOpen}
        close={() => setMissingDevicesReasonModalOpen(false)}
        handleConfirmMissingPartsReason={handleUpdateMissingPartsComment}
        handleCancelMissingPartsReason={() => setMissingDevices?.(true)}
      />
    </>
  );
};

export default PreImageCaptureInspection;
